/**
 * Validation (Section 9.5).
 *
 * Covers namespace rules, name-part rules, full-id uniqueness, reference
 * integrity, and the impact-count used by the namespace-change confirmation.
 * The hard cross-file consistency gate lives in the exporter (see
 * `export/consistency.ts`) because it inspects the serialized output.
 */

import {
  fullId,
  itemNameLangKey,
  guideCategoryLangKey,
  guideEntryLangKey,
  guideHomeTitleLangKey,
  guideIntroLangKey,
} from "./identifiers";
import type { ItemRef, Project, Recipe } from "./types";

export const NAMESPACE_RE = /^[a-z][a-z0-9_]*$/;
export const NAME_PART_RE = /^[a-z0-9_]+$/;

/** Namespaces that must never be used (vanilla). */
export const RESERVED_NAMESPACES = ["minecraft"];

/** Other well-known namespaces we warn (but don't block) on. */
export const DISCOURAGED_NAMESPACES = ["minecon", "misc", "seasons"];

export type Severity = "error" | "warning";

export interface Issue {
  severity: Severity;
  /** Machine code, e.g. "namespace.reserved". */
  code: string;
  message: string;
  /** Optional pointer to the offending object for UI navigation. */
  target?: { kind: "namespace" | "item" | "recipe" | "guide"; uuid?: string };
}

// --- Field-level validators ------------------------------------------------

export function validateNamespace(namespace: string): Issue[] {
  const issues: Issue[] = [];
  if (!namespace) {
    issues.push({ severity: "error", code: "namespace.empty", message: "Namespace is required.", target: { kind: "namespace" } });
    return issues;
  }
  if (!NAMESPACE_RE.test(namespace)) {
    issues.push({
      severity: "error",
      code: "namespace.format",
      message: "Namespace must match ^[a-z][a-z0-9_]*$ (start with a letter; lowercase letters, digits, underscores).",
      target: { kind: "namespace" },
    });
  }
  if (RESERVED_NAMESPACES.includes(namespace) || namespace.startsWith("minecraft")) {
    issues.push({
      severity: "error",
      code: "namespace.reserved",
      message: `"${namespace}" is a reserved vanilla namespace. Using it will conflict with or be rejected by the game.`,
      target: { kind: "namespace" },
    });
  }
  if (DISCOURAGED_NAMESPACES.includes(namespace)) {
    issues.push({
      severity: "warning",
      code: "namespace.discouraged",
      message: `"${namespace}" is a well-known namespace and may collide with other content.`,
      target: { kind: "namespace" },
    });
  }
  return issues;
}

export function validateNamePart(namePart: string): boolean {
  return NAME_PART_RE.test(namePart);
}

// --- Project-wide validation ----------------------------------------------

function pushDuplicate<T extends { uuid: string; id: string }>(
  issues: Issue[],
  objects: T[],
  kind: "item" | "recipe",
) {
  const seen = new Map<string, string>(); // id -> first uuid
  for (const obj of objects) {
    if (!validateNamePart(obj.id)) {
      issues.push({
        severity: "error",
        code: `${kind}.namepart`,
        message: `${kind} id "${obj.id || "(empty)"}" must match ^[a-z0-9_]+$.`,
        target: { kind, uuid: obj.uuid },
      });
      continue;
    }
    if (seen.has(obj.id)) {
      issues.push({
        severity: "error",
        code: `${kind}.duplicate`,
        message: `Duplicate ${kind} id "${obj.id}". Each ${kind} id must be unique.`,
        target: { kind, uuid: obj.uuid },
      });
    } else {
      seen.set(obj.id, obj.uuid);
    }
  }
}

function refIntegrity(project: Project, ref: ItemRef, recipe: Recipe, issues: Issue[], where: string) {
  if (ref.tag) {
    // A tag names a GROUP of items, so there is no single object to resolve: a
    // project tag may be carried by items this tool doesn't model (blocks from
    // the imported pack, for instance). Only the id shape is checkable.
    if (ref.kind === "vanilla" && !ref.id.includes(":")) {
      issues.push({
        severity: "error",
        code: "recipe.ref.malformed",
        message: `Recipe "${recipe.id}" ${where} references tag "${ref.id}" which is not a full namespace:name id.`,
        target: { kind: "recipe", uuid: recipe.uuid },
      });
    }
    if (ref.kind === "project" && !validateNamePart(ref.id)) {
      issues.push({
        severity: "error",
        code: "recipe.ref.malformed",
        message: `Recipe "${recipe.id}" ${where} references project tag "${ref.id || "(empty)"}", which must match ^[a-z0-9_]+$.`,
        target: { kind: "recipe", uuid: recipe.uuid },
      });
    }
    return;
  }
  if (ref.kind === "vanilla") {
    if (!ref.id.includes(":")) {
      issues.push({
        severity: "error",
        code: "recipe.ref.malformed",
        message: `Recipe "${recipe.id}" ${where} references "${ref.id}" which is not a full namespace:name id.`,
        target: { kind: "recipe", uuid: recipe.uuid },
      });
    }
    return; // vanilla refs pass through untouched
  }
  const exists = project.items.some((i) => i.id === ref.id) || ref.id === project.guideItemId;
  if (!exists) {
    issues.push({
      severity: "error",
      code: "recipe.ref.missing",
      message: `Recipe "${recipe.id}" ${where} references project item "${ref.id}", which does not exist under namespace "${project.namespace}".`,
      target: { kind: "recipe", uuid: recipe.uuid },
    });
  }
}

export function validateProject(project: Project): Issue[] {
  const issues: Issue[] = [];

  issues.push(...validateNamespace(project.namespace));

  // Guide item id
  if (!validateNamePart(project.guideItemId)) {
    issues.push({
      severity: "error",
      code: "guide.namepart",
      message: `Guide item id "${project.guideItemId || "(empty)"}" must match ^[a-z0-9_]+$.`,
      target: { kind: "guide" },
    });
  }

  // Name-part + duplicate checks per object type
  pushDuplicate(issues, project.items, "item");
  pushDuplicate(issues, project.recipes, "recipe");

  // Guide item id must not collide with a custom item id (they'd share NS:id).
  if (project.items.some((i) => i.id === project.guideItemId)) {
    issues.push({
      severity: "error",
      code: "guide.collision",
      message: `The guide item id "${project.guideItemId}" collides with a custom item of the same id.`,
      target: { kind: "guide" },
    });
  }

  // Full-id uniqueness across ALL items (including the guide item).
  const fullIds = new Map<string, number>();
  for (const id of [project.guideItemId, ...project.items.map((i) => i.id)]) {
    const composed = fullId(project.namespace, id);
    fullIds.set(composed, (fullIds.get(composed) ?? 0) + 1);
  }
  for (const [composed, count] of fullIds) {
    if (count > 1) {
      issues.push({
        severity: "error",
        code: "item.fullid.duplicate",
        message: `Full id "${composed}" is produced by more than one object.`,
      });
    }
  }

  // Reference integrity for recipes
  for (const recipe of project.recipes) {
    if (recipe.result.tag) {
      issues.push({
        severity: "error",
        code: "recipe.result.tag",
        message: `Recipe "${recipe.id}" produces tag "${recipe.result.id}". A result must be a single item, not a tag.`,
        target: { kind: "recipe", uuid: recipe.uuid },
      });
    }
    refIntegrity(project, recipe.result, recipe, issues, "result");
    if (recipe.shape === "shapeless") {
      recipe.ingredients.forEach((ref, i) => refIntegrity(project, ref, recipe, issues, `ingredient #${i + 1}`));
    } else {
      Object.entries(recipe.key).forEach(([k, ref]) => refIntegrity(project, ref, recipe, issues, `key "${k}"`));
    }
  }

  // Guide categories + entries: id format, uniqueness, and recipe references.
  const catIds = new Set<string>();
  for (const cat of project.guide.categories) {
    if (!validateNamePart(cat.id)) {
      issues.push({ severity: "error", code: "guide.category.namepart", message: `Guide category id "${cat.id || "(empty)"}" must match ^[a-z0-9_]+$.`, target: { kind: "guide" } });
    }
    if (cat.id === "main") {
      issues.push({ severity: "error", code: "guide.category.reserved", message: `Guide category id "main" is reserved for the homepage.`, target: { kind: "guide" } });
    }
    if (catIds.has(cat.id)) {
      issues.push({ severity: "error", code: "guide.category.duplicate", message: `Duplicate guide category id "${cat.id}".`, target: { kind: "guide" } });
    }
    catIds.add(cat.id);

    const entryIds = new Set<string>();
    for (const entry of cat.entries) {
      if (!validateNamePart(entry.id)) {
        issues.push({ severity: "error", code: "guide.entry.namepart", message: `Entry id "${entry.id || "(empty)"}" in category "${cat.id}" must match ^[a-z0-9_]+$.`, target: { kind: "guide" } });
      }
      if (entryIds.has(entry.id)) {
        issues.push({ severity: "error", code: "guide.entry.duplicate", message: `Duplicate entry id "${entry.id}" in category "${cat.id}".`, target: { kind: "guide" } });
      }
      entryIds.add(entry.id);

      if (entry.recipeId && !project.recipes.some((r) => r.id === entry.recipeId)) {
        issues.push({ severity: "warning", code: "guide.entry.recipe.missing", message: `Entry "${entry.id}" references recipe "${entry.recipeId}", which does not exist.`, target: { kind: "guide" } });
      }
    }
  }

  // At least one language
  if (project.languages.length === 0) {
    issues.push({ severity: "error", code: "lang.none", message: "The project needs at least one language." });
  }

  return issues;
}

export function hasBlockingErrors(issues: Issue[]): boolean {
  return issues.some((i) => i.severity === "error");
}

// --- Namespace-change impact (Section 9.4 confirmation) -------------------

export interface NamespaceImpact {
  items: number;
  recipes: number;
  langKeys: number;
  textureKeys: number;
}

/**
 * Count everything a namespace rename will re-derive, for the confirmation
 * dialog: "this will update N items, M recipes, K lang keys".
 */
export function namespaceImpact(project: Project): NamespaceImpact {
  const itemCount = project.items.length + 1; // + guide item
  const recipeCount = project.recipes.length;

  // The set of distinct lang keys is the same in every language file, so build
  // it once and multiply by the number of languages.
  const langKeySet = new Set<string>();
  langKeySet.add(itemNameLangKey(project.namespace, project.guideItemId));
  for (const item of project.items) langKeySet.add(itemNameLangKey(project.namespace, item.id));
  langKeySet.add(guideHomeTitleLangKey(project.namespace));
  langKeySet.add(guideIntroLangKey(project.namespace));
  for (const cat of project.guide.categories) {
    langKeySet.add(guideCategoryLangKey(project.namespace, cat.id, "title"));
    langKeySet.add(guideCategoryLangKey(project.namespace, cat.id, "body"));
    for (const entry of cat.entries) {
      langKeySet.add(guideEntryLangKey(project.namespace, cat.id, entry.id, "title"));
      langKeySet.add(guideEntryLangKey(project.namespace, cat.id, entry.id, "body"));
    }
  }
  const langKeys = langKeySet.size * Math.max(1, project.languages.length);

  const textureKeys = itemCount; // one short-name per item incl. guide

  return { items: itemCount, recipes: recipeCount, langKeys, textureKeys };
}
